import { useSocialLinks } from 'app/hooks/use-social-links';
import { FlatList, ScrollView } from 'react-native-gesture-handler';
import { isLoggedInAtom, userInfoAtom } from 'app/atoms';
import { useAtomValue } from 'jotai';
import { InsuredUser } from 'app/components/insured-users';
import { RatingCard } from 'app/components/rating-card';
import { ApproximateLocationMap } from 'app/components/approximate-location-map';
import { Column, Row } from 'app/design/primitives/box';
import { Button } from 'app/design/primitives/button';
import { Text } from 'app/design/primitives/text';
import { Thumbnail } from 'app/design/primitives/thumbnail';
import { localeAwareFormat, localeAwareFormatDistance, localeAwareFormatDuration } from 'app/lib/date';
import {
  useProfile,
  useProfileShares,
  useAddTrustedMember,
  useRemoveTrustedMember,
  useTrustedBy,
} from 'app/network/profile';
import { useStatistics } from 'app/network/statistics';
import { useDeleteRating, useReportRating } from 'app/network/loan';
import { SUPPORT_CHANNEL_URL } from 'app/network/chat/channel';
import { navigateToChat } from 'app/screens/chat/navigator-helper';
import { navigateToListing } from 'app/screens/billboard/navigator-helper';
import { memo, useEffect, useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Alert, Platform, Pressable, View } from 'react-native';
import Animated, {
  FadeIn,
  FadeInDown,
  LinearTransition,
  interpolateColor,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import {
  IconHeartHandshake,
  IconChevronRight,
  IconUserPlus,
  IconInfoCircle,
  IconChartBar,
  IconCaretUpFilled,
  IconChevronDown,
} from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { TrustInfoBottomSheet } from '../trust-info-bottom-sheet';
import { ShareActivityCard } from '../share-components';
import { theme, navigateToRatingEditor, type ProfileNavigation } from './shared';

type ProfileQuery = ReturnType<typeof useProfile>;
type StatisticsQuery = ReturnType<typeof useStatistics>;
export function ProfileTrustMembership({
  profileQuery,
  navigation,
  userId,
  isMyself,
}: {
  profileQuery: ProfileQuery;
  navigation: ProfileNavigation;
  userId: string;
  isMyself: boolean;
}) {
  const { t } = useTranslation();
  const socialLinks = useSocialLinks();
  const [showTrustInfo, setShowTrustInfo] = useState(false);
  const addTrustedMember = useAddTrustedMember();
  const removeTrustedMember = useRemoveTrustedMember();
  const { trustSummary, isTrusted, previewMembers, trustNames, remainingTrustMembers } = useTrustMembershipPreview(
    profileQuery,
    userId,
  );
  if (!socialLinks.enabled) return null;
  return (
    <>
      {previewMembers.length > 0 ? (
        <Pressable
          accessibilityRole="button"
          onPress={() => navigation.navigate('trusted-members', { mode: 'trusted-by', userId: userId })}
        >
          <Row alignItems="center" gap="spacing.general.x2">
            <Row alignItems="center">
              <Column
                alignItems="center"
                justifyContent="center"
                width={32}
                height={32}
                borderRadius="radius.sm"
                backgroundColor="surface.brand.secondary-4.subtle"
              >
                <IconHeartHandshake size={16} strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['text.primary']} />
              </Column>
              {previewMembers.map((member) => (
                <View
                  key={member.userId}
                  style={{
                    marginLeft: -10,
                    width: 32,
                    height: 32,
                    overflow: 'hidden',
                    borderRadius: 16,
                    borderWidth: 1,
                    borderColor: theme.colors['surface.default'],
                  }}
                >
                  <Thumbnail height={30} source={{ uri: member.picture ?? undefined }} />
                </View>
              ))}
            </Row>
            <Text flex={1} variant="bodySmall" style={{ lineHeight: 17 }}>
              <Trans
                ns="neighbour-profile"
                i18nKey={remainingTrustMembers ? 'trust.summaryMore' : 'trust.summary'}
                count={trustSummary?.count ?? 0}
                values={{ names: trustNames, remaining: remainingTrustMembers }}
                components={{ strong: <Text variant="bodySmall" fontWeight="700" /> }}
              />
            </Text>
          </Row>
        </Pressable>
      ) : null}

      {!isMyself ? (
        <Row alignItems="center" gap="spacing.general.x2">
          <Button
            authorize
            authorizationSource="member-profile"
            flex={1}
            minHeight={40}
            justifyContent="center"
            loading={addTrustedMember.isPending || removeTrustedMember.isPending}
            onPress={() =>
              trustSummary?.isTrustedByViewer ? removeTrustedMember.mutate(userId) : addTrustedMember.mutate(userId)
            }
            style={{
              borderRadius: 1000,
              backgroundColor:
                theme.colors[isTrusted ? 'component.button.bg.tertiary' : 'surface.brand.secondary-4.subtle'],
            }}
            variant="primary"
          >
            <Row alignItems="center" justifyContent="center" gap="spacing.general.x2">
              {isTrusted ? (
                <IconHeartHandshake color={theme.colors['text.emphasized']} size={20} strokeWidth={ICON_STROKE_WIDTH} />
              ) : (
                <IconUserPlus color="black" size={20} strokeWidth={ICON_STROKE_WIDTH} />
              )}
              <Text color={isTrusted ? 'text.emphasized' : 'text.primary'} fontWeight="600" variant="bodySmall">
                {t(isTrusted ? 'trust.trusted' : 'trust.add', {
                  ns: 'neighbour-profile',
                })}
              </Text>
            </Row>
          </Button>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t('trust.info', { ns: 'neighbour-profile' })}
            hitSlop={8}
            onPress={() => setShowTrustInfo(true)}
            style={{ width: 32, height: 40, alignItems: 'center', justifyContent: 'center' }}
          >
            <IconInfoCircle color={theme.colors['text.primary']} size={20} strokeWidth={ICON_STROKE_WIDTH} />
          </Pressable>
        </Row>
      ) : null}
      <TrustInfoBottomSheet
        open={showTrustInfo}
        onClose={() => setShowTrustInfo(false)}
        onManage={() => navigation.navigate('trusted-members', { mode: 'circle' })}
      />
    </>
  );
}
function useTrustMembershipPreview(profileQuery: ProfileQuery, userId: string) {
  const trustSummary = profileQuery.data?.trust;
  const isTrusted = Boolean(trustSummary?.isTrustedByViewer);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const trustedBy = useTrustedBy(userId, { enabled: isLoggedIn && Boolean(trustSummary?.count) });
  const previewMembers = trustedBy.data
    ? [...trustedBy.data].sort((a, b) => Number(b.isInViewerTrustCircle) - Number(a.isInViewerTrustCircle)).slice(0, 3)
    : (trustSummary?.members.slice(0, 3) ?? []);
  const trustNames = previewMembers.map((member) => member.firstName).join(', ');
  const remainingTrustMembers = Math.max(0, (trustSummary?.count ?? 0) - previewMembers.length);
  return { trustSummary, isTrusted, previewMembers, trustNames, remainingTrustMembers };
}

function GoodToKnowCards({ children, multiple }: { children: ReactNode; multiple: boolean }) {
  if (!multiple) return <Column width="100%">{children}</Column>;

  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={{ gap: 8, paddingHorizontal: theme.spacing['spacing.general.x4'] }}
      style={{ marginHorizontal: -theme.spacing['spacing.general.x4'] }}
    >
      {children}
    </ScrollView>
  );
}

export function ProfileTrustOverview({
  profileQuery,
  statisticsQuery,
  navigation,
  userId,
  isMyself,
  onReviews,
}: {
  profileQuery: ProfileQuery;
  statisticsQuery: StatisticsQuery;
  navigation: ProfileNavigation;
  userId: string;
  isMyself: boolean;
  onReviews: () => void;
}) {
  const { t } = useTranslation();
  const userInfo = useAtomValue(userInfoAtom);
  const canRateAfterLoans = isFeatureEnabled(FeatureFlag.rateAfterLoans, userInfo?.featureFlags);
  const reduceMotion = useReducedMotion();
  const recentSharesQuery = useProfileShares(userId, { scope: 'all', role: 'all', take: 10 });
  const commonSharesQuery = useProfileShares(userId, { scope: 'common', role: 'all', take: 3 }, { enabled: !isMyself });
  return (
    <Column gap="spacing.general.x5" paddingHorizontal="spacing.general.x4" paddingTop="spacing.general.x5">
      <ProfileOverviewStats {...{ profileQuery, statisticsQuery, canRateAfterLoans }} />

      <Animated.View
        layout={reduceMotion ? undefined : LinearTransition.duration(260)}
        style={{ gap: theme.spacing['spacing.general.x5'] }}
      >
        <Column gap="spacing.general.x2">
          <Row alignItems="center" justifyContent="space-between">
            <Text fontWeight="700" variant="bodyXLarge">
              {t('shares.recentTitle', { ns: 'neighbour-profile' })}
            </Text>
            <Pressable onPress={() => navigation.navigate('profile-shares', { userId: userId, scope: 'all' })}>
              <Row alignItems="center" gap="spacing.general.x1">
                <Text color="text.brand" fontWeight="600" variant="bodySmall">
                  {t('seeMore', { ns: 'neighbour-profile' })}
                </Text>
                <IconChevronRight color={theme.colors['text.brand']} size={16} />
              </Row>
            </Pressable>
          </Row>
          <FlatList
            horizontal
            data={recentSharesQuery.data?.results.slice(0, 6) ?? []}
            keyExtractor={(share) => share.id}
            initialNumToRender={6}
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ gap: 8, paddingHorizontal: theme.spacing['spacing.general.x4'] }}
            style={{ marginHorizontal: -theme.spacing['spacing.general.x4'], flexGrow: 0 }}
            renderItem={({ item: share }) => (
              <ShareActivityCard share={share} onPress={() => navigateToListing(navigation, share.listing.id)} />
            )}
          />
          {recentSharesQuery.data?.results.length === 0 ? (
            <Text color="text.tertiary">{t('shares.empty', { ns: 'neighbour-profile' })}</Text>
          ) : null}
        </Column>

        <ProfileRecentRatings {...{ profileQuery, navigation, onReviews }} />

        <ProfileGoodToKnow {...{ profileQuery, commonSharesQuery, navigation, userId }} />
      </Animated.View>

      {!isMyself && profileQuery.data?.publicLocation ? (
        <ApproximateLocationMap
          location={profileQuery.data.publicLocation}
          locations={profileQuery.data.publicLocations}
          title={t('location.profileTitle', {
            ns: 'neighbour-profile',
            count: profileQuery.data.publicLocations?.length ?? 1,
          })}
          hint={t('location.privacyHint', { ns: 'neighbour-profile' })}
          privacyMessage={t('location.securityMessage', { ns: 'my-profile' })}
        />
      ) : null}
    </Column>
  );
}
function ProfileOverviewStats({
  profileQuery,
  statisticsQuery,
  canRateAfterLoans,
}: {
  profileQuery: ProfileQuery;
  statisticsQuery: StatisticsQuery;
  canRateAfterLoans: boolean;
}) {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const [showFullStats, setShowFullStats] = useState(false);
  const sharingCount =
    (statisticsQuery.data?.loans.incoming.total ?? 0) + (statisticsQuery.data?.loans.outgoing.total ?? 0);
  return (
    <Animated.View layout={reduceMotion ? undefined : LinearTransition.duration(260)}>
      <CompactProfileStats
        canRateAfterLoans={canRateAfterLoans}
        recommendations={statisticsQuery.data?.loans.trustPoints ?? 0}
        shares={sharingCount}
        responseRate={statisticsQuery.data?.chat?.responseRate}
        responseTime={statisticsQuery.data?.chat?.averageResponseTime}
        lent={statisticsQuery.data?.loans.outgoing.total ?? 0}
        borrowed={statisticsQuery.data?.loans.incoming.total ?? 0}
        requests={statisticsQuery.data?.listings.requests.total ?? 0}
        memberSince={
          profileQuery.data?.createdAt
            ? localeAwareFormat(new Date(profileQuery.data.createdAt), t('dateFormat_MMMYYYY', { ns: 'common' }))
            : '-'
        }
        expanded={showFullStats}
        onToggle={() => setShowFullStats((value) => !value)}
      />
    </Animated.View>
  );
}

function ProfileRecentRatings({
  profileQuery,
  navigation,
  onReviews,
}: {
  profileQuery: ProfileQuery;
  navigation: ProfileNavigation;
  onReviews: () => void;
}) {
  const { t } = useTranslation();
  const userInfo = useAtomValue(userInfoAtom);
  const canRateAfterLoans = isFeatureEnabled(FeatureFlag.rateAfterLoans, userInfo?.featureFlags);
  const ratings = canRateAfterLoans ? (profileQuery.data?.ratings ?? []) : [];
  const { mutate: deleteRecentRating } = useDeleteRating({
    onSuccess: async () => {
      await profileQuery.refetch();
    },
  });
  const { mutate: reportRecentRating } = useReportRating({
    onSuccess: () =>
      navigateToChat(navigation, SUPPORT_CHANNEL_URL, { i18nKey: 'reportRatingPrompt', ns: 'neighbour-profile' }),
  });
  type ProfileRating = NonNullable<NonNullable<ProfileQuery['data']>['ratings']>[number];
  const editRating = (rating: ProfileRating) => navigateToRatingEditor(navigation, rating);
  const confirmDeleteRating = (rating: ProfileRating) => {
    if (Platform.OS === 'web') {
      if (
        globalThis.confirm(
          `${t('reviews.actions.deleteConfirmTitle', { ns: 'neighbour-profile' })}\n\n${t('reviews.actions.deleteConfirmMessage', { ns: 'neighbour-profile' })}`,
        )
      ) {
        deleteRecentRating(rating.loanId);
      }
      return;
    }
    Alert.alert(
      t('reviews.actions.deleteConfirmTitle', { ns: 'neighbour-profile' }),
      t('reviews.actions.deleteConfirmMessage', { ns: 'neighbour-profile' }),
      [
        {
          text: t('reviews.actions.deleteConfirmCancel', { ns: 'neighbour-profile' }),
          style: 'cancel',
        },
        {
          text: t('reviews.actions.deleteConfirmOk', { ns: 'neighbour-profile' }),
          style: 'destructive',
          onPress: () => deleteRecentRating(rating.loanId),
        },
      ],
    );
  };
  return (
    <>
      {canRateAfterLoans && ratings.length ? (
        <Column gap="spacing.general.x2">
          <Row alignItems="center" justifyContent="space-between">
            <Text fontWeight="700" variant="bodyXLarge">
              {t('reviews.recentTitle', { ns: 'neighbour-profile' })}
            </Text>
            <Pressable onPress={() => onReviews()}>
              <Row alignItems="center" gap="spacing.general.x1">
                <Text color="text.brand" fontWeight="600" variant="bodySmall">
                  {t('seeMore', { ns: 'neighbour-profile' })}
                </Text>
                <IconChevronRight color={theme.colors['text.brand']} size={16} />
              </Row>
            </Pressable>
          </Row>
          <FlatList
            horizontal
            data={ratings.slice(0, 4)}
            keyExtractor={(rating) => rating.loanId}
            initialNumToRender={4}
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ gap: 12, paddingHorizontal: theme.spacing['spacing.general.x4'] }}
            style={{ marginHorizontal: -theme.spacing['spacing.general.x4'], flexGrow: 0 }}
            renderItem={({ item: rating }) => (
              <RatingCard
                compact
                currentUserId={typeof userInfo === 'object' && userInfo ? userInfo.id : undefined}
                forcedWidth={260}
                hideListing
                hideTopLine
                onDeleteRating={() => confirmDeleteRating(rating)}
                onEditRating={() => editRating(rating)}
                onReportRating={() => reportRecentRating(rating.loanId)}
                rating={rating}
              />
            )}
          />
        </Column>
      ) : null}
    </>
  );
}

function ProfileGoodToKnow({
  profileQuery,
  commonSharesQuery,
  navigation,
  userId,
}: {
  profileQuery: ProfileQuery;
  commonSharesQuery: ReturnType<typeof useProfileShares>;
  navigation: ProfileNavigation;
  userId: string;
}) {
  const { t } = useTranslation();
  const hasBothGoodToKnowCards = Boolean(profileQuery.data?.insured && commonSharesQuery.data?.summary.total);
  if (!profileQuery.data?.insured && !commonSharesQuery.data?.summary.total) return null;
  return (
    <Column gap="spacing.general.x3">
      <Text fontWeight="600" variant="bodyLarge">
        {t('goodToKnow', { ns: 'neighbour-profile' })}
      </Text>
      <GoodToKnowCards multiple={hasBothGoodToKnowCards}>
        {profileQuery.data?.insured ? (
          <InsuredUser
            otherUser={{ insured: true, profile: { firstName: profileQuery.data.firstName } }}
            placement="profile"
            variant="good-to-know"
            style={{ width: hasBothGoodToKnowCards ? 209 : '100%' }}
          />
        ) : null}
        {commonSharesQuery.data?.summary.total ? (
          <Pressable
            onPress={() =>
              navigation.navigate('profile-shares', {
                userId: userId,
                scope: 'common',
              })
            }
            style={{
              width: hasBothGoodToKnowCards ? 209 : '100%',
              padding: 24,
              borderRadius: 16,
              backgroundColor: '#F8F7F5',
            }}
          >
            <Column gap="spacing.general.x2">
              <Column
                alignItems="center"
                justifyContent="center"
                style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#F7DE72' }}
              >
                <IconChartBar color={theme.colors['text.primary']} size={24} strokeWidth={ICON_STROKE_WIDTH} />
              </Column>
              <Text fontSize={14} lineHeight={17}>
                <Trans
                  ns="neighbour-profile"
                  i18nKey="shares.commonWidget"
                  values={{
                    count: commonSharesQuery.data.summary.total,
                    name: profileQuery.data?.firstName,
                    ago: commonSharesQuery.data.summary.lastSharedAt
                      ? localeAwareFormatDistance(new Date(), new Date(commonSharesQuery.data.summary.lastSharedAt))
                      : '',
                  }}
                  components={{
                    strong: <Text fontSize={14} lineHeight={17} fontWeight="700" />,
                  }}
                />
              </Text>
              <Text color="text.brand" fontSize={14} fontWeight="600">
                {t('shares.viewHistory', { ns: 'neighbour-profile' })} ›
              </Text>
            </Column>
          </Pressable>
        ) : null}
      </GoodToKnowCards>
    </Column>
  );
}

const CompactProfileStats = memo(function CompactStats({
  canRateAfterLoans,
  recommendations,
  shares,
  responseRate,
  responseTime,
  lent,
  borrowed,
  requests,
  memberSince,
  expanded,
  onToggle,
}: {
  canRateAfterLoans: boolean;
  recommendations: number;
  shares: number;
  responseRate?: number | null;
  responseTime?: number | null;
  lent: number;
  borrowed: number;
  requests: number;
  memberSince: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const { t } = useTranslation('neighbour-profile');
  const reduceMotion = useReducedMotion();
  const expansionProgress = useSharedValue(expanded ? 1 : 0);

  useEffect(() => {
    expansionProgress.set(reduceMotion ? Number(expanded) : withTiming(Number(expanded), { duration: 260 }));
  }, [expanded, expansionProgress, reduceMotion]);

  const cardStyle = useAnimatedStyle(() => ({
    backgroundColor: interpolateColor(
      expansionProgress.get(),
      [0, 1],
      [theme.colors['surface.tertiary-neutral'], theme.colors['surface.tertiary-neutral']],
    ),
    borderRadius: 16 + 16 * expansionProgress.get(),
  }));
  const stats = [
    ...(canRateAfterLoans
      ? [{ value: `👍 ${recommendations.toFixed(0)}`, label: t('stats.recommendationsShort') }]
      : []),
    { value: String(shares), label: t('stats.shares') },
    {
      value: responseRate === null || responseRate === undefined ? '-' : `${responseRate.toFixed(0)}%`,
      label: t('stats.responseRateShort'),
    },
    {
      value:
        responseTime === null || responseTime === undefined ? '-' : localeAwareFormatDuration(responseTime, true, true),
      label: t('stats.responseTimeShort'),
    },
  ];
  const expandedStats = [
    { value: String(lent), label: t('stats.lent') },
    { value: String(borrowed), label: t('stats.borrowed') },
    { value: String(requests), label: t('stats.requests') },
    { value: memberSince, label: t('stats.memberSince') },
  ];

  return (
    <Animated.View
      layout={reduceMotion ? undefined : LinearTransition.duration(260)}
      style={[cardStyle, { overflow: 'hidden' }]}
    >
      <Pressable accessibilityRole="button" onPress={onToggle} style={{ padding: 16 }}>
        <Row gap="spacing.general.x1">
          {stats.map((stat) => (
            <Column key={stat.label} alignItems="center" flex={1} gap="spacing.general.x1">
              <Text fontWeight="600" variant="bodyLarge">
                {stat.value}
              </Text>
              <Text color="text.secondary" fontSize={11} textAlign="center">
                {stat.label}
              </Text>
            </Column>
          ))}
        </Row>

        {expanded ? (
          <Animated.View entering={reduceMotion ? undefined : FadeInDown.duration(220)} style={{ marginTop: 24 }}>
            <Row gap="spacing.general.x1">
              {expandedStats.map((stat) => (
                <Column key={stat.label} alignItems="center" flex={1} gap="spacing.general.x1">
                  <Text fontWeight="600" variant="bodyLarge" textAlign="center">
                    {stat.value}
                  </Text>
                  <Text color="text.secondary" fontSize={11} textAlign="center">
                    {stat.label}
                  </Text>
                </Column>
              ))}
            </Row>
          </Animated.View>
        ) : null}

        <Row
          alignItems="center"
          justifyContent="center"
          gap="spacing.general.x1"
          style={{ marginTop: expanded ? 24 : 12 }}
        >
          <Text color="text.emphasized" fontWeight="600" variant="bodySmall">
            {t(expanded ? 'stats.less' : 'stats.seeMore')}
          </Text>
          <Animated.View
            key={expanded ? 'expanded' : 'collapsed'}
            entering={reduceMotion ? undefined : FadeIn.duration(140)}
          >
            {expanded ? (
              <IconCaretUpFilled color={theme.colors['text.emphasized']} size={14} />
            ) : (
              <IconChevronDown color={theme.colors['text.emphasized']} size={16} />
            )}
          </Animated.View>
        </Row>
      </Pressable>
    </Animated.View>
  );
});
